import { useCallback } from "react";
import { VideoPlatform, VideoSDKAdapter } from "../video-sdk";
import { baseVideoSDKAdapterAtom } from "../video-sdk/core/atoms";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { ZoomVideoSDKAdapter } from "../video-sdk/zoom/adapter";
import { useIsZoomEnabled } from "./useIsZoomEnabled";

/**
 * Returns the current video platform, based on the initialized video SDK adapter.
 */
export function useVideoPlatform(): [
  VideoPlatform | null,
  () => VideoPlatform | null,
] {
  const videoSDKAdapter = useAtomValue(baseVideoSDKAdapterAtom);
  const getVideoSDKAdapter = useAtomCallback(
    useCallback((get) => get(baseVideoSDKAdapterAtom), []),
  );

  const getVideoPlatformCallback = useCallback(() => {
    const videoSDKAdapter = getVideoSDKAdapter();
    if (videoSDKAdapter === null) {
      return null;
    }
    return getVideoPlatform(videoSDKAdapter);
  }, [getVideoSDKAdapter]);

  return [getVideoPlatform(videoSDKAdapter), getVideoPlatformCallback];
}

/**
 * Returns the desired video platform, based on the Statsig gate and any overrides..
 */
export function useDesiredVideoPlatform(): [
  VideoPlatform,
  () => VideoPlatform,
] {
  const [isZoomEnabled, checkIsZoomEnabled] = useIsZoomEnabled();
  const desiredVideoPlatform = getDesiredVideoPlatform(isZoomEnabled);

  const getDesiredVideoPlatformCallback = useCallback(
    () => getDesiredVideoPlatform(checkIsZoomEnabled()),
    [checkIsZoomEnabled],
  );

  return [desiredVideoPlatform, getDesiredVideoPlatformCallback];
}

// utils

function getVideoPlatform(
  videoSDKAdapter: VideoSDKAdapter | null,
): VideoPlatform | null {
  if (!videoSDKAdapter) return null;

  return videoSDKAdapter instanceof ZoomVideoSDKAdapter
    ? VideoPlatform.Zoom
    : VideoPlatform.Twilio;
}

function getDesiredVideoPlatform(isZoomEnabled: boolean): VideoPlatform {
  return isZoomEnabled ? VideoPlatform.Zoom : VideoPlatform.Twilio;
}
